import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  UploadState,
  ProgressModal,
  uploadToScreenApp,
  useIframeResizer,
} from './common';

const Upload: React.FC = () => {
  const { t } = useTranslation();
  useIframeResizer();

  const [state, setState] = useState<UploadState>({
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      uploadToScreenApp(
        file,
        file.name,
        () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () =>
          setState((prev) => ({
            ...prev,
            uploadComplete: true,
            uploading: false,
          })),
        (error) => setState((prev) => ({ ...prev, error, uploading: false }))
      );
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        const file = files[0];
        uploadToScreenApp(
          file,
          file.name,
          () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
          (progress) => setState((prev) => ({ ...prev, progress })),
          () =>
            setState((prev) => ({
              ...prev,
              uploadComplete: true,
              uploading: false,
            })),
          (error) => setState((prev) => ({ ...prev, error, uploading: false }))
        );
      }
    },
    []
  );

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ p: 3, maxWidth: '60rem', width: '100%' }}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            borderStyle: 'dashed',
            borderWidth: 3,
            borderRadius: '12px',
            bgcolor: isDragging ? 'action.hover' : 'background.paper',
            borderColor: isDragging ? 'primary.main' : 'secondary.dark',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="video/*,audio/*,image/*,.m4a,audio/m4a,audio/x-m4a"
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              '@keyframes floatAnimation': {
                '0%': {
                  transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                },
                '50%': {
                  transform: isDragging
                    ? 'scale(1.1) translateY(-5px)'
                    : 'scale(1) translateY(-5px)',
                },
                '100%': {
                  transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                },
              },
            }}
          >
            <CloudUploadIcon
              sx={{
                fontSize: 56,
                color: isDragging ? 'primary.main' : 'text.secondary',
                transition: 'all 0.2s ease-in-out',
                transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                '.MuiPaper-root:hover &': {
                  animation: 'floatAnimation 2s ease-in-out infinite',
                },
              }}
            />

            <Typography variant="h5" fontWeight="medium" sx={{ mb: 1 }}>
              {t('upload.title')}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {t('upload.subtitle')}
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {t('upload.supportText')}
            </Typography>
          </Box>

          {state.error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {state.error}
            </Typography>
          )}
        </Paper>

        <ProgressModal
          open={state.uploading}
          progress={state.progress}
          isComplete={state.uploadComplete}
          processingMessage={t('upload.processing')}
          completeMessage={t('upload.complete')}
        />
      </Box>
    </Box>
  );
};

export default Upload;
